import api from './api'

const getBasePath = () => {
  return '/cookie-consent'
}

export default {
  /**
   * Store a new cookie consent log
   * @param {Object} consentData - The consent data to log
   * @returns {Promise} API response
   */
  async logConsent(consentData) {
    return api.execute('post', `${getBasePath()}/logs`, consentData)
  },

  /**
   * Get paginated consent logs (Manager only)
   * @param {Object} params - Query parameters for filtering and pagination
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.search - Search by email
   * @param {string} params.type - Filter by consent type (accepted/rejected/customized)
   * @param {string} params.startDate - Filter from date
   * @param {string} params.endDate - Filter to date
   * @param {string} params.sortBy - Sort field
   * @param {string} params.sortOrder - Sort direction (asc/desc)
   * @returns {Promise} API response with logs and pagination
   */
  async getLogs(params = {}) {
    const queryParams = new URLSearchParams()
    
    // Add parameters if they exist
    if (params.page) queryParams.append('page', params.page)
    if (params.limit) queryParams.append('limit', params.limit)
    if (params.search) queryParams.append('search', params.search)
    if (params.type) queryParams.append('type', params.type)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.sortBy) queryParams.append('sortBy', params.sortBy)
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
    
    const queryString = queryParams.toString()
    const url = queryString ? `${getBasePath()}/logs?${queryString}` : `${getBasePath()}/logs`
    
    return api.execute('get', url)
  },

  /**
   * Get consent statistics (Manager only)
   * @param {Object} params - Query parameters
   * @param {string} params.period - Time period (today/week/month/year/all)
   * @param {string} params.startDate - Custom period start date
   * @param {string} params.endDate - Custom period end date
   * @returns {Promise} API response with statistics
   */
  async getStats(params = {}) {
    const queryParams = new URLSearchParams()
    
    if (params.period) queryParams.append('period', params.period)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    
    const queryString = queryParams.toString()
    const url = queryString ? `${getBasePath()}/stats?${queryString}` : `${getBasePath()}/stats`
    
    return api.execute('get', url)
  },

  /**
   * Export consent logs (Manager only)
   * @param {Object} params - Export parameters
   * @param {string} params.format - Export format (csv/xlsx)
   * @param {string} params.search - Search filter
   * @param {string} params.type - Consent type filter
   * @param {string} params.startDate - Filter from date
   * @param {string} params.endDate - Filter to date
   * @returns {Promise} File download response
   */
  async exportLogs(params = {}) {
    const queryParams = new URLSearchParams()
    
    queryParams.append('format', params.format || 'csv')
    if (params.search) queryParams.append('search', params.search)
    if (params.type) queryParams.append('type', params.type)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    
    const url = `${getBasePath()}/export?${queryParams.toString()}`
    
    // For file downloads, we need to handle the response differently
    try {
      const response = await api.execute('get', url)
      
      // Create a blob from the response
      const blob = new Blob([response], { 
        type: params.format === 'xlsx' 
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          : 'text/csv' 
      })
      
      // Create download link
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `consent-logs-${new Date().toISOString().split('T')[0]}.${params.format || 'csv'}`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
      
      return { success: true, message: 'Export started successfully' }
    } catch (error) {
      console.error('Export failed:', error)
      throw error
    }
  },

  /**
   * Get single consent log details (Manager only)
   * @param {string} logId - The log ID to retrieve
   * @returns {Promise} API response with log details
   */
  async getLogDetails(logId) {
    return api.execute('get', `${getBasePath()}/logs/${logId}`)
  },

  /**
   * Delete old consent logs for GDPR compliance (Manager only)
   * @param {Object} params - Cleanup parameters
   * @param {number} params.olderThan - Delete logs older than X days
   * @param {boolean} params.dryRun - Preview without deleting
   * @returns {Promise} API response with cleanup stats
   */
  async cleanupOldLogs(params) {
    const queryParams = new URLSearchParams()
    
    if (params.olderThan) queryParams.append('olderThan', params.olderThan)
    if (params.dryRun !== undefined) queryParams.append('dryRun', params.dryRun)
    
    const url = `${getBasePath()}/logs/cleanup?${queryParams.toString()}`
    
    return api.execute('delete', url)
  },

  /**
   * Get current user's consent history
   * @returns {Promise} API response with user's consent history
   */
  async getMyHistory() {
    return api.execute('get', `${getBasePath()}/my-history`)
  },

  /**
   * Send pending consent logs after user login
   * This is called automatically after successful login
   * @returns {Promise} API response
   */
  async sendPendingLogs() {
    const PENDING_CONSENT_LOG = 'pending_consent_log'
    const pendingLog = localStorage.getItem(PENDING_CONSENT_LOG)
    
    if (pendingLog) {
      try {
        const logData = JSON.parse(pendingLog)
        const response = await this.logConsent(logData)
        
        if (response && response.success) {
          // Clear pending log after successful submission
          localStorage.removeItem(PENDING_CONSENT_LOG)
          console.log('Pending consent log sent successfully')
        }
        
        return response
      } catch (error) {
        console.error('Error sending pending consent log:', error)
        throw error
      }
    }
    
    return { success: true, message: 'No pending logs to send' }
  },

  /**
   * Process consent type from categories
   * @param {Object} categories - Consent categories object
   * @returns {string} Consent type (accepted/rejected/customized)
   */
  getConsentType(categories) {
    if (!categories) return 'unknown'
    
    const { analytics, marketing, preferences } = categories
    
    if (analytics && marketing && preferences) {
      return 'accepted'
    } else if (!analytics && !marketing && !preferences) {
      return 'rejected'
    } else {
      return 'customized'
    }
  },

  /**
   * Format consent data for display
   * @param {Object} logData - Raw log data
   * @returns {Object} Formatted log data
   */
  formatLogData(logData) {
    return {
      ...logData,
      consentType: this.getConsentType(logData.categories),
      formattedDate: new Date(logData.timestamp).toLocaleString(),
      deviceInfo: this.parseUserAgent(logData.userAgent)
    }
  },

  /**
   * Parse user agent string for display
   * @param {string} userAgent - User agent string
   * @returns {Object} Parsed browser and OS info
   */
  parseUserAgent(userAgent) {
    if (!userAgent) {
      return { browser: 'Unknown', os: 'Unknown', device: 'Unknown' }
    }
    
    let browser = 'Unknown'
    let os = 'Unknown'
    let device = 'Desktop'
    
    // Browser detection
    if (userAgent.includes('Chrome')) browser = 'Chrome'
    else if (userAgent.includes('Safari')) browser = 'Safari'
    else if (userAgent.includes('Firefox')) browser = 'Firefox'
    else if (userAgent.includes('Edge')) browser = 'Edge'
    else if (userAgent.includes('Opera')) browser = 'Opera'
    
    // OS detection
    if (userAgent.includes('Windows')) os = 'Windows'
    else if (userAgent.includes('Mac')) os = 'macOS'
    else if (userAgent.includes('Linux')) os = 'Linux'
    else if (userAgent.includes('Android')) {
      os = 'Android'
      device = 'Mobile'
    } else if (userAgent.includes('iPhone')) {
      os = 'iOS'
      device = 'Mobile'
    } else if (userAgent.includes('iPad')) {
      os = 'iPadOS'
      device = 'Tablet'
    }
    
    return { browser, os, device }
  },

  /**
   * Validate consent data before submission
   * @param {Object} consentData - Consent data to validate
   * @returns {Object} Validation result
   */
  validateConsentData(consentData) {
    const errors = []
    
    if (!consentData.timestamp) {
      errors.push('Timestamp is required')
    }
    
    if (!consentData.categories) {
      errors.push('Categories object is required')
    } else {
      const requiredCategories = ['necessary', 'analytics', 'marketing', 'preferences']
      requiredCategories.forEach(cat => {
        if (typeof consentData.categories[cat] !== 'boolean') {
          errors.push(`Category ${cat} must be a boolean`)
        }
      })
    }
    
    if (!consentData.sessionId && !consentData.userEmail) {
      errors.push('Either sessionId or userEmail is required')
    }
    
    return {
      isValid: errors.length === 0,
      errors
    }
  },

  /**
   * Get summary statistics from logs
   * @param {Array} logs - Array of consent logs
   * @returns {Object} Summary statistics
   */
  calculateSummaryStats(logs) {
    if (!logs || logs.length === 0) {
      return {
        total: 0,
        acceptanceRate: 0,
        rejectionRate: 0,
        customizationRate: 0
      }
    }
    
    const total = logs.length
    const accepted = logs.filter(log => this.getConsentType(log.categories) === 'accepted').length
    const rejected = logs.filter(log => this.getConsentType(log.categories) === 'rejected').length
    const customized = logs.filter(log => this.getConsentType(log.categories) === 'customized').length
    
    return {
      total,
      acceptanceRate: ((accepted / total) * 100).toFixed(1),
      rejectionRate: ((rejected / total) * 100).toFixed(1),
      customizationRate: ((customized / total) * 100).toFixed(1),
      accepted,
      rejected,
      customized
    }
  }
}